import { useEffect, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { fetchClients } from '../lib/dataService';
import type { ClientItem } from '../types';

const DEFAULT_LOGO = '/images/clients/default-logo.svg';

const container: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

function ClientCard({ client }: { client: ClientItem }) {
  return (
    <motion.div variants={item} className="group flex flex-col items-center rounded-xl border border-transparent bg-white p-6 transition-all duration-300 hover:-translate-y-1.5 hover:border-cyan-400 hover:shadow-xl hover:shadow-cyan-500/10"  >
      <div className="flex h-24 w-full items-center justify-center">
        <img src={client.logo} alt={client.name} loading="lazy" className="h-24 w-[95%] object-contain transition-transform duration-300 group-hover:scale-105" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = DEFAULT_LOGO; }} />
      </div>
      <p className="mt-4 text-center text-sm text-slate-300">{client.name}</p>
    </motion.div>
  );
}

export default function Clients() {
  const [clients, setClients] = useState<ClientItem[]>([]);

  useEffect(() => {
    let cancelled = false;
    fetchClients().then((data) => {
      if (!cancelled) setClients(data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section id="clients" className="bg-slate-900 py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.4 }} variants={container} className="mx-auto max-w-2xl text-center" >
          <motion.p variants={item} className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-400" >
            Clients
          </motion.p>
          <motion.h2 variants={item} className="mt-3 text-3xl font-bold text-white sm:text-4xl lg:text-5xl" >
            服务客户
          </motion.h2>
          <motion.p variants={item} className="mt-4 text-slate-400">
            信赖源于实力，我们与众多政府单位与企业客户携手同行
          </motion.p>
        </motion.div>

        <motion.div initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.1 }} variants={container} className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4" >
          {clients.map((client) => (
            <ClientCard key={client.id} client={client} />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
